import { useMemo } from "react";
import { Building2, ShieldAlert, ShieldCheck, CalendarX2 } from "lucide-react";
import type { ProjectWorkspace } from "../types/domain";

interface ContractorsViewProps {
  workspace: ProjectWorkspace;
}

export function ContractorsView({ workspace }: ContractorsViewProps) {
  const contractors = workspace.contractors || [];

  const nonCompliant = useMemo(() => {
    return contractors.filter((c) => c.status === "non-compliant");
  }, [contractors]);

  return (
    <div className="main-panel">
      <header className="page-header">
        <span className="eyebrow">Supply Chain Risk Graph</span>
        <h1>Contractor Compliance Gateway</h1>
        <p>
          Live gatekeeping of organizational readiness. Non-compliant contractors automatically propagate failure states through their assigned HIRARC controls and Design Risks, blocking Field Verification and Permits-to-Work.
        </p>
      </header>

      {nonCompliant.length > 0 && (
        <div className="alert-banner error-banner" style={{ margin: "1rem 2rem", background: "var(--red-soft)", padding: "1rem", borderRadius: "8px", border: "1px solid #fecaca", display: "flex", gap: "1rem", alignItems: "center" }}>
          <ShieldAlert color="var(--red)" />
          <div>
            <strong>Dynamic Risk Propagation Active</strong>
            <p style={{ margin: 0, color: "var(--red)" }}>
              {nonCompliant.length} contractor(s) have fallen out of compliance. Their associated critical controls and design risks have been automatically flagged as unassured across the project graph.
            </p>
          </div>
        </div>
      )}

      <div className="content-pad">
        <div className="audit-list" style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))" }}>
          {contractors.length === 0 ? (
            <div className="empty-state">
              <Building2 size={48} color="var(--line)" />
              <h3>No contractors onboarded</h3>
              <p>Invite your supply chain to begin automated compliance tracking.</p>
            </div>
          ) : (
            contractors.map((contractor) => (
              <div 
                key={contractor.id} 
                className="auth-card" 
                style={{ 
                  padding: "1.5rem", 
                  borderTop: contractor.status === "verified" ? "4px solid var(--teal)" : "4px solid var(--red)"
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
                  <div>
                    <h3 style={{ margin: "0 0 0.25rem 0" }}>{contractor.name}</h3>
                    <span className="status-pill" style={{ textTransform: "capitalize", fontSize: "11px" }}>
                      {contractor.tier}
                    </span>
                  </div>
                  {contractor.status === "verified" ? (
                    <ShieldCheck color="var(--teal)" />
                  ) : (
                    <CalendarX2 color="var(--red)" />
                  )}
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", background: "var(--surface)", padding: "1rem", borderRadius: "var(--radius-md)" }}>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: "12px", color: "var(--muted)" }}>Readiness Score</span>
                    <strong style={{ fontSize: "24px", color: contractor.readinessScore >= 90 ? "var(--teal)" : "var(--red)" }}>
                      {contractor.readinessScore}%
                    </strong>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                    <span style={{ fontSize: "12px", color: "var(--muted)" }}>Evidence</span>
                    <strong style={{ fontSize: "24px" }}>{contractor.evidence.length}</strong>
                  </div>
                </div>

                <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem" }}>
                  <button className="button button-primary" style={{ flex: 1, padding: "0.5rem" }}>
                    View Profile
                  </button>
                  {contractor.status === "non-compliant" && (
                    <button className="button" style={{ flex: 1, padding: "0.5rem", background: "var(--red-soft)", color: "var(--red)", border: "none" }}>
                      Issue BCF
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
